import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useSettingsHandlers } from '@/hooks/useSettingsHandlers';
import { MembershipSection } from '@/components/settings/MembershipSection';
import { PersonalInfoCard } from '@/components/settings/PersonalInfoCard';
import { NotificationsCard } from '@/components/settings/NotificationsCard';
import { SecurityCard } from '@/components/settings/SecurityCard';
import { LanguageCard } from '@/components/settings/LanguageCard';
import { LogoutSection } from '@/components/settings/LogoutSection';
import { SettingsHeader } from '@/components/settings/SettingsHeader';
import { AIConsentManager } from '@/components/ai-assistant/components/AIConsentManager';
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useProfileData } from '@/hooks/settings/useProfileData';
import { useSubscriptionData } from '@/hooks/settings/useSubscriptionData';

export default function Settings() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { profile, isLoading: isProfileLoading, error: profileError, updateProfile } = useProfileData();
  const { data: subscriptionPlans, isLoading: isPlansLoading } = useSubscriptionData();
  const { loading, handleSubscribe, handleLogout } = useSettingsHandlers();

  React.useEffect(() => {
    if (searchParams.get('success')) {
      toast({
        title: "Betaling vellykket",
        description: "Ditt abonnement har blitt aktivert.",
      });
    }
    if (searchParams.get('canceled')) {
      toast({
        title: "Betaling avbrutt",
        description: "Betalingen ble avbrutt. Prøv igjen senere.",
      });
    }
  }, [searchParams, toast]);

  if (isProfileLoading || isPlansLoading) {
    return (
      <div className="min-h-screen w-full p-3 sm:p-4 md:p-6 overflow-x-hidden bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-2xl mx-auto space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-48 w-full" />
          <div className="grid gap-3 md:gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="min-h-screen w-full p-3 sm:p-4 md:p-6 bg-gradient-to-b from-orange-50 to-white">
        <Alert variant="destructive" className="max-w-2xl mx-auto">
          <AlertDescription>
            Det oppstod en feil ved lasting av profilen din. Prøv å laste siden på nytt.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-2xl mx-auto p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6">
        <SettingsHeader />
        
        <div className="space-y-3 md:space-y-4">
          <div 
            className="w-full overflow-visible"
            role="region"
            aria-label="Medlemskap seksjonen"
          >
            <MembershipSection 
              profile={profile} 
              loading={loading} 
              onSubscribe={handleSubscribe}
            />
          </div>

          <div 
            className="grid gap-3 md:gap-4"
            role="region"
            aria-label="Innstillinger seksjonen"
          >
            <PersonalInfoCard 
              profile={profile} 
              onUpdate={updateProfile} 
            />
            <NotificationsCard 
              profile={profile} 
              onUpdate={updateProfile} 
            />
            <SecurityCard 
              profile={profile} 
              onUpdate={updateProfile} 
            />
            <LanguageCard 
              profile={profile} 
              onUpdate={updateProfile} 
            />
            <div className="w-full overflow-visible">
              <AIConsentManager />
            </div>
            <LogoutSection 
              loading={loading} 
              onLogout={handleLogout} 
            />
          </div>
        </div>
      </div>
    </div>
  );
}